import {
  useCallback,
  useRef,
  type ChangeEvent,
  type InputHTMLAttributes,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  type Ref,
} from 'react';
import { IcnClose } from '../../icons/index.js';
import { useI18nT } from '../../i18n/index.js';

export interface InputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'prefix' | 'className' | 'size'
> {
  prefix?: ReactNode;
  suffix?: ReactNode;
  invalid?: boolean;
  size?: 'sm' | 'md' | 'lg';
  /** Show a trailing × that clears the value when non-empty. */
  clearable?: boolean;
  clearLabel?: string;
  className?: string;
  ref?: Ref<HTMLInputElement>;
  onValueChange?: (value: string) => void;
  onClear?: () => void;
}

/** Single-line text input with optional prefix/suffix slots and clearable ×.
 *  Native input remains — text/email/password/number/search/tel/url are
 *  standard DOM primitives, not system-UI pickers (unlike color/date/time/
 *  range which would be prohibited). Autofill background is normalized via
 *  CSS so Chrome's yellow highlight doesn't leak through themes. */
export function Input(props: InputProps): ReactNode {
  const t = useI18nT();
  const {
    prefix,
    suffix,
    invalid = false,
    size = 'md',
    clearable = false,
    clearLabel = t('input.clearLabel'),
    className,
    ref,
    onValueChange,
    onClear,
    onChange,
    disabled,
    readOnly,
    value,
    defaultValue,
    ...rest
  } = props;

  const internalRef = useRef<HTMLInputElement | null>(null);

  const setRef = useCallback(
    (node: HTMLInputElement | null) => {
      internalRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref !== undefined && ref !== null) {
        (ref as { current: HTMLInputElement | null }).current = node;
      }
    },
    [ref],
  );

  const stateAttr =
    disabled === true ? 'disabled' : readOnly === true ? 'readonly' : invalid ? 'invalid' : 'idle';

  const classes = ['vq-input', className].filter(Boolean).join(' ');

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    onChange?.(e);
    onValueChange?.(e.target.value);
  };

  const handleClear = (e: ReactMouseEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    onClear?.();
    onValueChange?.('');
    const el = internalRef.current;
    if (el !== null) {
      // Uncontrolled path: reset the DOM value + refocus for flow.
      if (value === undefined) el.value = '';
      el.focus();
    }
  };

  // Determine if a value is present so `clearable` can conditionally render
  // the ×. Works for both controlled (value prop) and uncontrolled (DOM).
  const hasValue =
    value !== undefined
      ? String(value).length > 0
      : defaultValue !== undefined
        ? String(defaultValue).length > 0
        : false;

  const showClear = clearable && hasValue && disabled !== true && readOnly !== true;

  return (
    <span
      className={classes}
      data-component="Input"
      data-size={size}
      data-state={stateAttr}
      data-has-prefix={prefix !== undefined ? 'true' : undefined}
      data-has-suffix={suffix !== undefined || showClear ? 'true' : undefined}
    >
      {prefix !== undefined ? (
        <span className="vq-input__prefix" data-slot="prefix" aria-hidden="true">
          {prefix}
        </span>
      ) : null}
      <input
        {...rest}
        ref={setRef}
        disabled={disabled}
        readOnly={readOnly}
        value={value}
        defaultValue={defaultValue}
        aria-invalid={invalid || undefined}
        onChange={handleChange}
        className="vq-input__native"
      />
      {showClear ? (
        <button
          type="button"
          className="vq-input__clear"
          aria-label={clearLabel}
          onClick={handleClear}
          tabIndex={-1}
        >
          <IcnClose size={12} />
        </button>
      ) : null}
      {suffix !== undefined ? (
        <span className="vq-input__suffix" data-slot="suffix" aria-hidden="true">
          {suffix}
        </span>
      ) : null}
    </span>
  );
}
